const AI_WS_URL = import.meta.env.VITE_APP_BASE_AI_WS
import {ElMessage} from 'element-plus'

export default class WebSocketRecorder {
    constructor() {
        this.socketUrl = AI_WS_URL;
        this.ws = null;
        this.audioContext = null;
        this.stream = null; // 麦克风访问权限
        this.scriptProcessor = null; // 媒体流源 脚本处理器
        this.isConnected = false; //ws 是否连接
        this.isRecording = false; // 是否正在录音
        this.onTranscript = null; // 识别结果回调
        this.pcmBuffer = []; //录音缓冲区
        this.interval = 200; // 发送频率 200ms发送一次数据包
        this.audioConfig = {
            sampleRate: 16000, //采样率
            channelCount: 1, // 1单声道  - 直接使用第一个声道   2立体声 - 混合左右声道
            bufferSize: 4096,
            bitDepth: 16 //位深度 16位或32位
        };
        this.currentPartialText = ''; // 当前部分识别结果
        this.finalTexts = []; // 已确认的最终识别结果
    }

    // 连接WebSocket并初始化
    async connectWS() {
        try {
            return new Promise((resolve, reject) => {
                if (this.isConnected) {
                    resolve();
                    return;
                }

                this.ws = new WebSocket(this.socketUrl);

                this.ws.onopen = () => {
                    this.isConnected = true;
                    console.log('✅ WebSocket 已连接');
                    resolve();
                };

                this.ws.onmessage = (event) => {
                    const msg = JSON.parse(event.data);
                    if (this.onTranscript) {
                        if (msg.type === 'partial') {
                            this.currentPartialText = msg.text || '';
                            this.onTranscript({
                                type: 'partial',
                                text: this.currentPartialText
                            });
                        } else if (msg.type === 'final') {
                            const finalText = msg.text || '';
                            if (finalText && !this.finalTexts.includes(finalText)) {
                                this.finalTexts.push(finalText);
                            }
                            this.currentPartialText = '';
                            this.onTranscript({
                                type: 'final',
                                text: this.finalTexts.join(' ')
                            });
                        } else if (msg.type === 'ready') {
                            console.log('后端会话 ready，开始发送音频帧');
                        } else if (msg.type === 'error') {
                            console.error('后端错误：', msg.message);
                            ElMessage.error(`语音识别错误：${msg.message}`);
                        } else if (msg.type === 'done') {
                            console.log('后端完成会话');
                            this.currentPartialText = '';
                            this.finalTexts = [];
                        } else {
                            console.log('收到消息：', msg);
                        }
                    }
                };

                this.ws.onerror = (error) => {
                    this.isConnected = false;
                    console.error("WebSocket 错误:", error);
                    ElMessage.error('WebSocket 连接失败，请检查服务器地址');
                    reject(error);
                };

                this.ws.onclose = () => {
                    console.log("WebSocket 断开")
                    this.isConnected = false;
                };
            });
        } catch (err) {
            console.error('WebSocket 连接失败:', err);
            ElMessage.error('无法连接到语音识别服务器');
            return '连接失败'
        }
    }

    // 开始录音
    async startRecording() {
        try {
            console.log('🎤 开始录音流程...');
            if (!this.isConnected) {
                console.log('正在连接 WebSocket...');
                await this.connectWS();
            }

            if (!this.isConnected) {
                ElMessage.error("录音连接失败")
                return 'viocefail'
            }
            this.currentPartialText = '';
            this.finalTexts = [];

            // 发送消息通知开始录音
            this.ws.send(JSON.stringify({
                type: 'start',
                model: 'paraformer-realtime-v2',
                format: 'pcm',
                sampleRate: this.audioConfig.sampleRate
            }));

            try {
                console.log('正在获取麦克风权限...');
                this.stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        channelCount: this.audioConfig.channelCount,
                        sampleRate: this.audioConfig.sampleRate
                    }
                });
                console.log('✅ 麦克风权限已获取');
            } catch (err) {
                console.log('getUserMedia 错误：', err);
                this.closeWS()
                console.log('无法获取麦克风权限');
                ElMessage.error('无法获取麦克风权限，请在浏览器设置中允许访问');
                return 'viocefail'
            }

            this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: this.audioConfig.sampleRate
            });

            // 创建媒体流源
            const source = this.audioContext.createMediaStreamSource(this.stream);

            // 创建脚本处理器
            this.scriptProcessor = this.audioContext.createScriptProcessor(
                this.audioConfig.bufferSize,
                this.audioConfig.channelCount,
                this.audioConfig.channelCount
            );

            // 设置音频处理回调
            this.scriptProcessor.onaudioprocess = (event) => {
                if (!this.isRecording) return;

                const inputBuffer = event.inputBuffer;
                this._processAudio(inputBuffer);
            };

            // 连接节点
            source.connect(this.scriptProcessor);
            this.scriptProcessor.connect(this.audioContext.destination);

            this.isRecording = true;
            this.pcmBuffer = [];
            // 设置定时器
            this._setupInterval();

            console.log('✅ 录音已开始');

            return 'success'
        } catch (error) {
            console.error("录音启动失败:", error);
            if (this.isConnected) this.closeWS()
            ElMessage.error(`录音失败：${error.message}`);
            return 'viocefail'
        }
    }
    // 停止录音
    stopRecording() {
        if (this.isRecording) {
            this.isRecording = false;

            this.ws.send(JSON.stringify({type: 'stop'}));
            // 断开音频节点
            try {
                this.scriptProcessor && this.scriptProcessor.disconnect();
            } catch(error) {
            }
            // 停止媒体流
            try {
                this.audioContext && this.audioContext.close();
            } catch(error) {
            }
            try {
                this.stream && this.stream.getTracks().forEach(t => t.stop());
            } catch(error) {
            }

            // 清除定时器
            if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
            }

            // 清空缓冲区
            this.pcmBuffer = [];

            this.scriptProcessor = null
            this.audioContext = null
            this.stream = null

            // 2秒后关闭WebSocket
            setTimeout(() => {
                this.closeWS()
            }, 2000);
        }
    }

    closeWS() {
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.isConnected = false;
    }

    // 发送PCM帧
    _sendPCMFrame() {
        if (this.pcmBuffer.length === 0) return;

        // 合并缓冲区中的所有PCM数据
        const frameSize = this.getFrameSize();
        const buffer = new Uint8Array(frameSize);
        let offset = 0;

        while (this.pcmBuffer.length > 0 && offset < frameSize) {
            const chunk = this.pcmBuffer[0];
            const remaining = frameSize - offset;

            if (chunk.byteLength <= remaining) {
                buffer.set(new Uint8Array(chunk), offset);
                offset += chunk.byteLength;
                this.pcmBuffer.shift();
            } else {
                buffer.set(new Uint8Array(chunk.slice(0, remaining)), offset);
                this.pcmBuffer[0] = chunk.slice(remaining);
                offset = frameSize;
            }
        }

        // 发送帧
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(buffer);
        }
    }

    // 计算帧大小
    getFrameSize() {
        // 帧大小 = (采样率 * 声道数 * 位深度 / 8) * (间隔时间 / 1000)
        const bytesPerSample = this.audioConfig.bitDepth / 8;
        const samplesPerFrame = (this.audioConfig.sampleRate * this.interval) / 1000;
        return Math.floor(samplesPerFrame * this.audioConfig.channelCount * bytesPerSample);
    }

    // 处理音频数据
    _processAudio(inputBuffer) {
        // 根据配置处理不同声道数
        let data;
        if (this.audioConfig.channelCount === 1) {
            // 单声道 - 直接使用第一个声道
            data = inputBuffer.getChannelData(0);
        } else {
            // 立体声 - 混合左右声道
            const left = inputBuffer.getChannelData(0);
            const right = inputBuffer.getChannelData(1);
            data = new Float32Array(left.length);
            for (let i = 0; i < left.length; i++) {
                data[i] = (left[i] + right[i]) / 2;
            }
        }

        // 根据位深度转换数据
        let pcmData;
        if (this.audioConfig.bitDepth === 16) {
            pcmData = this._floatTo16BitPCM(data);
        } else {
            pcmData = this._floatTo32BitPCM(data);
        }
        // 添加到缓冲区
        this.pcmBuffer.push(pcmData.buffer);
    }

    // 将Float32转换为16位PCM
    _floatTo16BitPCM(input) {
        const output = new Int16Array(input.length);
        for (let i = 0; i < input.length; i++) {
            const s = Math.max(-1, Math.min(1, input[i]));
            output[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        return output;
    }

    // 将Float32转换为32位PCM
    _floatTo32BitPCM(input) {
        const output = new Int32Array(input.length);
        for (let i = 0; i < input.length; i++) {
            const s = Math.max(-1, Math.min(1, input[i]));
            output[i] = s * 0x7FFFFFFF;
        }
        return output;
    }

    // 设置定时器
    _setupInterval() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }

        if (this.isRecording) {
            this.intervalId = setInterval(() => {
                this._sendPCMFrame();
            }, this.interval);
        }
    }
}

